import React, { type FC, type ReactNode, type PropsWithChildren } from 'react';

import type { FieldRenderProps } from 'react-final-form';

import FieldWrapper from '../field-wrapper/field-wrapper';
import Text from '../../text/text';

export interface CheckboxGroupProps extends FieldRenderProps<string> {
  children?: ReactNode;
  /** Allows the component to get additional styles. */
  className?: string;
  /** Determines whether the text should be light or dark */
  hasDarkBackground?: boolean;
  /** The instructions for the checkbox. */
  instructions?: string;
  /** Determines whether to use Dark Mode styles */
  isDarkMode?: boolean;
  /** Checks if the field is disabled. */
  isDisabled?: boolean;
  /** Checks if the field is hidden. */
  isHidden?: boolean;
  /** Checks if the field is required to submit. */
  isRequired?: boolean;
  /** Checks if the instructions should be hidden. */
  shouldHideInstructions?: boolean;
}

const CheckboxGroup: FC<PropsWithChildren<CheckboxGroupProps>> = ({
  children,
  className,
  customErrorMessage,
  hasDarkBackground = false,
  id,
  instructions,
  isDarkMode = false,
  isDisabled = false,
  isFullWidth = false,
  isHidden = false,
  isRequired = false,
  label,
  shouldHideInstructions = false,
  meta,
  ...rest
}) => {
  const hasError = meta?.error && meta?.touched;
  const errorMessage = customErrorMessage || meta?.error;
  const labelId = `checkboxGroup-${id}`;
  const hasInstructions = !!instructions;

  return (
    <FieldWrapper
      {...rest}
      isCheckboxGroup
      aria-labelledby={labelId}
      css={{
        display: 'flex',
        flexDirection: 'column',
      }}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
      customErrorMessage={errorMessage}
      hasDarkBackground={hasDarkBackground}
      hasError={hasError}
      id={id}
      isDarkMode={isDarkMode}
      isDisabled={isDisabled}
      isFullWidth={isFullWidth}
      isHidden={isHidden}
      isRequired={isRequired}
      label={label}
      labelId={labelId}
      labelVariant="labelS700"
    >
      {children}
      {hasInstructions && !shouldHideInstructions && (
        <Text
          css={theme => [
            {
              order: 3,
              marginTop: 0,
              marginLeft: theme.space.s40,
              width: '100%',
            },
            isDarkMode && {
              color: theme.colors.white,
            },
          ]}
          variant="paragraphS400"
        >
          {instructions}
        </Text>
      )}
    </FieldWrapper>
  );
};

export { CheckboxGroup };
export default CheckboxGroup;
